"use strict";
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const webStorage = require("./web-storage-1mQqDY5f.js");
const react = require("react");
const tryInitStorage = (accessType, key, defaultValue) => {
  if (typeof window !== "undefined") {
    const init = webStorage.webStorage.get({ accessType, key, returnString: false });
    if (typeof init === "string") {
      try {
        const parsedInit = JSON.parse(init);
        return parsedInit;
      } catch (e) {
        return init;
      }
    }
    if ((init === null || init === void 0) && defaultValue !== void 0) {
      webStorage.webStorage.set({ accessType, key, value: defaultValue });
      return defaultValue;
    }
    return init;
  }
  return void 0;
};
const getHookId = () => {
  var _a, _b, _c;
  if (typeof window !== "undefined" && window.crypto !== void 0 && ((_a = window.crypto) == null ? void 0 : _a.randomUUID) !== void 0) {
    return (_c = (_b = window.crypto) == null ? void 0 : _b.randomUUID()) != null ? _c : Date.now().toString();
  }
  return Date.now().toString();
};
const useWebStorage = (accessType, key, defaultValue = void 0) => {
  const [storageValue, setStorageValue] = react.useState(tryInitStorage(accessType, key, defaultValue));
  const isMounted = react.useRef(false);
  const hookId = react.useRef(getHookId());
  const lifeCycleEventKey = `thdStorageUtils.${accessType === webStorage.LOCAL_STORAGE ? "localStorage" : "sessionStorage"}`;
  react.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  react.useEffect(() => {
    const val = webStorage.webStorage.get({ accessType, key, returnString: false });
    setStorageValue(val);
  }, [accessType, key]);
  react.useEffect(() => {
    if (typeof window === "undefined" || typeof window.LIFE_CYCLE_EVENT_BUS === "undefined") {
      return () => {
      };
    }
    const eventHandler = ({ output }) => {
      const { key: eventStorageKey, hookId: eventHookId } = output != null ? output : {};
      if (eventHookId !== hookId.current && eventStorageKey === key) {
        const val = webStorage.webStorage.get({ accessType, key, returnString: false });
        if (isMounted.current) {
          setStorageValue(val);
        }
      }
    };
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on(lifeCycleEventKey, eventHandler);
    return () => {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off(lifeCycleEventKey, eventHandler);
    };
  }, [accessType, key, setStorageValue]);
  const wrappedSetStorageValue = (value) => {
    if (value === null || value === void 0) {
      webStorage.webStorage.remove({ accessType, key });
      setStorageValue(void 0);
    } else {
      webStorage.webStorage.set({ accessType, key, value });
      const newValue = webStorage.webStorage.get({ accessType, key, returnString: false });
      setStorageValue(newValue);
    }
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(lifeCycleEventKey, {
      hookId: hookId.current,
      key
    });
  };
  return [storageValue, wrappedSetStorageValue];
};
const openDB = (dbName, version, upgradeCB) => {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && typeof window.indexedDB !== "undefined") {
      const dbOpenRequest = window.indexedDB.open(dbName, version);
      dbOpenRequest.onupgradeneeded = upgradeCB;
      dbOpenRequest.onerror = reject;
      dbOpenRequest.onsuccess = (event) => {
        resolve(event.target.result);
      };
    } else {
      reject(new Error("indexedDB not available"));
    }
  });
};
const initDB = (dbName, config) => {
  return openDB(dbName, config.version, (event) => {
    const db = event.target.result;
    Object.entries(config.stores).forEach(([objectStoreName, { storeConfig, indexes = [] }]) => {
      if (!db.objectStoreNames.contains(objectStoreName)) {
        const objectStore = db.createObjectStore(objectStoreName, storeConfig);
        indexes.forEach(({ name: indexName, unique }) => {
          objectStore.createIndex(indexName, indexName, { unique });
        });
      }
    });
  });
};
const TRANSACTION_TYPES = {
  READ: "readonly",
  WRITE: "readwrite"
};
const openTransaction = (db, storeName, transactionType, onsuccess, onerror) => {
  const transaction = db.transaction(storeName, transactionType);
  transaction.onerror = onerror;
  transaction.onsuccess = onsuccess;
  return transaction;
};
const getObjectStore = (db, storeName, transactionType, onsuccess, onerror) => {
  const transaction = openTransaction(db, storeName, transactionType, onsuccess, onerror);
  return transaction.objectStore(storeName);
};
const getDBFunctions = (dbName, version, storeName) => {
  const getByKey = (key) => {
    return new Promise((resolve, reject) => {
      openDB(dbName, version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.READ, resolve, reject);
        const request = store.get(key);
        request.onsuccess = (event) => {
          resolve(event.target.result);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const getAll = () => {
    return new Promise((resolve, reject) => {
      openDB(dbName, version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.READ, resolve, reject);
        const request = store.getAll();
        request.onerror = (event) => {
          reject(new Error(`Error fetching all data for: ${storeName}: ${event.target.error || ""}`));
        };
        request.onsuccess = (event) => resolve(event.target.result);
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const add = (value, key) => {
    return new Promise((resolve, reject) => {
      openDB(dbName, version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const request = store.add(value, key);
        request.onerror = (event) => {
          reject(new Error(`Error adding data for: ${storeName}: ${event.target.error || ""}`));
        };
        request.onsuccess = (event) => {
          resolve(event.target.result);
        };
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const remove = (key) => {
    return new Promise((resolve, reject) => {
      openDB(dbName, version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const request = store.delete(key);
        request.onsuccess = resolve;
        request.onerror = reject;
      });
    });
  };
  const update = (value, key) => {
    return new Promise((resolve, reject) => {
      openDB(dbName, version).then((db) => {
        const transaction = openTransaction(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const store = transaction.objectStore(storeName);
        transaction.oncomplete = () => {
          resolve();
        };
        store.put(value, key);
      }).catch((err) => {
        reject(new Error(`Error opening DB ${err}`));
      });
    });
  };
  const clear = () => {
    return new Promise((resolve, reject) => {
      openDB(dbName, version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        const request = store.clear();
        request.onsuccess = resolve;
        request.onerror = reject;
      }).catch((err) => reject(new Error(`Error opening DB ${err}`)));
    });
  };
  const cursor = (cb, keyRange, direction) => {
    return new Promise((resolve, reject) => {
      openDB(dbName, version).then((db) => {
        const store = getObjectStore(db, storeName, TRANSACTION_TYPES.WRITE, resolve, reject);
        store.openCursor(keyRange, direction).onsuccess = (event) => {
          const iDBCursor = event.target.result;
          if (iDBCursor) {
            const response = cb(event);
            if (response === "DONE") resolve();
          } else resolve();
        };
      });
    });
  };
  return {
    getByKey,
    getAll,
    add,
    remove,
    clear,
    update,
    cursor
  };
};
const commonDBInfo = {
  dbName: null,
  version: void 0,
  dbAvailable: null
};
const initIndexDB = (dbName, config) => __async(exports, null, function* () {
  commonDBInfo.dbName = dbName;
  commonDBInfo.version = config.version;
  return initDB(dbName, config).then(() => {
    commonDBInfo.dbAvailable = true;
  }).catch(() => {
    commonDBInfo.dbAvailable = false;
  });
});
const useIndexedDB = (objectStoreName, dbName) => {
  return react.useMemo(
    () => getDBFunctions(commonDBInfo.dbName || dbName, commonDBInfo.version, objectStoreName),
    [objectStoreName]
  );
};
/**
 * @preserve
 * @public
 * @function useLocalStorage
 * @description React hook that allows access to local storage values,
 *  the same way you would use a useState hook.
 * @param {string} key - Local Storage object key
 * @returns {[string | Object, (string | object) => void]}
 *  - Returns the value as a string or object, and a function to update the value
 */
const useLocalStorage = (key, defaultValue = void 0) => {
  return useWebStorage(webStorage.LOCAL_STORAGE, key, defaultValue);
};
/**
 * @preserve
 * @public
 * @function useSessionStorage
 * @description React hook that allows access to session storage values,
 *  the same way you would use a useState hook.
 * @param {string} key - Local Storage object key
 * @returns {[string | Object, (string | object) => void]}
 *  - Returns the value as a string or object, and a function to update the value
 */
const useSessionStorage = (key, defaultValue = void 0) => {
  return useWebStorage(webStorage.SESSION_STORAGE, key, defaultValue);
};
exports.initIndexDB = initIndexDB;
exports.useIndexedDB = useIndexedDB;
exports.useLocalStorage = useLocalStorage;
exports.useSessionStorage = useSessionStorage;
